// src/data/blogData.js

export const blogPosts = [
    {
      id: 1,
      title: "Getting Started with React",
      excerpt: "Learn the basics of React and start building modern web applications...",
      author: "Jane Smith",
      date: "March 1, 2024",
      category: "Technology",
      readTime: "8 min read",
      content: `
React has revolutionized the way we build user interfaces. As a JavaScript library for building user interfaces, it has become one of the most popular choices among developers worldwide.

## Why React?

React's component-based architecture makes it easy to manage complex UIs by breaking them down into smaller, reusable pieces. Here are some key benefits:

1. **Component-Based**: Build encapsulated components that manage their own state.
2. **Declarative**: Design simple views for each state in your application.
3. **Learn Once, Write Anywhere**: Develop new features without rewriting existing code.

## Getting Started

To start using React, you'll need to have Node.js installed on your system. Once you have Node.js, you can create a new React application using Create React App:

\`\`\`bash
npx create-react-app my-app
cd my-app
npm start
\`\`\`

## Components and Props

Components are the building blocks of any React application. A component is a JavaScript class or function that accepts inputs (called "props") and returns a React element that describes how a section of the UI should appear.

\`\`\`jsx
function Welcome(props) {
    return <h1>Hello, {props.name}</h1>;
}
\`\`\`

## State and Lifecycle

State allows React components to change their output over time in response to user actions, network responses, and anything else. Here's a simple example:

\`\`\`jsx
function Counter() {
    const [count, setCount] = useState(0);

    return (
    <div>
        <p>You clicked {count} times</p>
        <button onClick={() => setCount(count + 1)}>
        Click me
        </button>
    </div>
    );
}
\`\`\`
`
    },
    {
      id: 2,
      title: "The Future of Web Development",
      excerpt: "Exploring upcoming trends and technologies that will shape the web...",
      author: "John Doe",
      date: "March 5, 2024",
      category: "Technology",
      readTime: "6 min read",
      content: `
The landscape of web development is constantly evolving. As we look towards the future, several exciting trends and technologies are emerging that will shape how we build web applications.

## Emerging Technologies

1. **Web Assembly**: Enabling high-performance code execution in browsers
2. **Edge Computing**: Bringing computation closer to data sources
3. **AI-Powered Development**: Integrating artificial intelligence into development workflows

## New Frameworks and Tools

The JavaScript ecosystem continues to evolve with new frameworks and tools designed to make development more efficient and enjoyable. Here are some key trends:

1. **Meta-Frameworks**: Next.js, Remix, and other full-stack frameworks
2. **Server Components**: React Server Components and similar technologies
3. **Build Tools**: Vite, Turbopack, and other next-gen bundlers

## The Rise of AI in Development

Artificial Intelligence is reshaping how we approach web development:

1. **Code Generation**: AI-powered code suggestions and automation
2. **Testing**: Automated test generation and optimization
3. **Design Systems**: AI-assisted component creation and styling

## Performance and User Experience

Future web development will focus heavily on:

1. **Core Web Vitals**: Optimizing for Google's performance metrics
2. **Progressive Enhancement**: Building robust, accessible applications
3. **Device Adaptation**: Responding to diverse device capabilities
`
    },
    {
      id: 3,
      title: "Mastering CSS Grid",
      excerpt: "A comprehensive guide to using CSS Grid for modern layouts...",
      author: "Sarah Johnson",
      date: "March 10, 2024",
      category: "Design",
      readTime: "10 min read",
      content: `
CSS Grid has transformed how we approach web layouts. This powerful system allows for complex, responsive designs with minimal code.

## Understanding Grid Basics

CSS Grid introduces two main concepts: grid containers and grid items. Here's how to get started:

1. **Grid Container**: The parent element that holds grid items
2. **Grid Items**: The direct children of the grid container
3. **Grid Lines**: The dividing lines that create the grid structure

## Common Grid Patterns

Let's explore some common layout patterns you can achieve with CSS Grid:

1. **Holy Grail Layout**: Header, footer, and three columns
2. **Card Grid**: Responsive grid of cards
3. **Magazine Layout**: Complex, asymmetrical designs

## Advanced Grid Techniques

CSS Grid offers powerful features for complex layouts:

1. **Grid Template Areas**: Named grid areas for intuitive layouts
2. **Auto-fit and Auto-fill**: Dynamic column creation
3. **Subgrid**: Alignment with parent grid lines

## Responsive Grid Design

Creating responsive layouts with Grid:

1. **Minmax()**: Flexible sizing with minimums and maximums
2. **Media Queries**: Adapting grid layouts to screen sizes
3. **Grid Auto Flow**: Controlling item placement
`
    },
    {
      id: 4,
      title: "JavaScript Fundamentals",
      excerpt: "A beginner's guide to JavaScript and modern web development...",
      author: "Alice Brown",
      date: "March 15, 2024",
      category: "Development",
      readTime: "7 min read",
      content: `
JavaScript is a versatile and powerful programming language that is essential for modern web development. Whether you're building interactive websites, server-side applications, or mobile apps, understanding JavaScript fundamentals is crucial.

## What is JavaScript?

JavaScript is a high-level, interpreted programming language that enables you to create dynamic and interactive web content. It is one of the core technologies of the web, alongside HTML and CSS.

## Key Features of JavaScript

1. **Interactivity**: JavaScript allows you to create interactive elements on web pages, such as forms, animations, and games.
2. **Versatility**: It can be used for both client-side and server-side development.
3. **Event-Driven**: JavaScript can respond to user actions, such as clicks and key presses.
4. **Asynchronous Programming**: With features like Promises and async/await, JavaScript can handle asynchronous operations efficiently.

## Basic Syntax

Here are some fundamental concepts and syntax in JavaScript:

### Variables

Variables are used to store data. You can declare variables using 'var', 'let', or 'const'.

\`\`\`javascript
let message = "Hello, World!";
\`\`\`

### Functions

Functions are blocks of reusable code. You can define functions using the 'function' keyword.

\`\`\`javascript
function greet(name) {
    return "Hello, " + name + "!";
}
\`\`\`
    `
    },
    {
        id: 5,
        title: "JavaScript Fundamentals 2",
        excerpt: "A beginner's guide to JavaScript and modern web development...",
        author: "Alice Brown",
        date: "March 20, 2024",
        category: "Development",
        readTime: "7 min read",
        content: `
JavaScript is a versatile and powerful programming language that is essential for modern web development. Whether you're building interactive websites, server-side applications, or mobile apps, understanding JavaScript fundamentals is crucial.

## What is JavaScript?

JavaScript is a high-level, interpreted programming language that enables you to create dynamic and interactive web content. It is one of the core technologies of the web, alongside HTML and CSS.

## Key Features of JavaScript

1. **Interactivity**: JavaScript allows you to create interactive elements on web pages, such as forms, animations, and games.
2. **Versatility**: It can be used for both client-side and server-side development.
3. **Event-Driven**: JavaScript can respond to user actions, such as clicks and key presses.
4. **Asynchronous Programming**: With features like Promises and async/await, JavaScript can handle asynchronous operations efficiently.

## Basic Syntax

Here are some fundamental concepts and syntax in JavaScript:

### Variables

Variables are used to store data. You can declare variables using 'var', 'let', or 'const'.

\`\`\`javascript
let message = "Hello, World!";
\`\`\`

### Functions

Functions are blocks of reusable code. You can define functions using the 'function' keyword.

\`\`\`javascript
function greet(name) {
    return "Hello, " + name + "!";
}
\`\`\`
      `
      },
      {
        id: 6,
        title: "JavaScript Fundamentals 3",
        excerpt: "A beginner's guide to JavaScript and modern web development...",
        author: "Alice Brown",
        date: "March 25, 2024",
        category: "Development",
        readTime: "7 min read",
        content: `
JavaScript is a versatile and powerful programming language that is essential for modern web development. Whether you're building interactive websites, server-side applications, or mobile apps, understanding JavaScript fundamentals is crucial.

## What is JavaScript?

JavaScript is a high-level, interpreted programming language that enables you to create dynamic and interactive web content. It is one of the core technologies of the web, alongside HTML and CSS.

## Key Features of JavaScript

1. **Interactivity**: JavaScript allows you to create interactive elements on web pages, such as forms, animations, and games.
2. **Versatility**: It can be used for both client-side and server-side development.
3. **Event-Driven**: JavaScript can respond to user actions, such as clicks and key presses.
4. **Asynchronous Programming**: With features like Promises and async/await, JavaScript can handle asynchronous operations efficiently.

## Basic Syntax

Here are some fundamental concepts and syntax in JavaScript:

### Variables

Variables are used to store data. You can declare variables using 'var', 'let', or 'const'.

\`\`\`javascript
let message = "Hello, World!";
\`\`\`

### Functions

Functions are blocks of reusable code. You can define functions using the 'function' keyword.

\`\`\`javascript
function greet(name) {
    return "Hello, " + name + "!";
}
\`\`\`
      `
      },
      {
        id: 7,
        title: "JavaScript Fundamentals 4",
        excerpt: "A beginner's guide to JavaScript and modern web development...",
        author: "Alice Brown",
        date: "March 30, 2024",
        category: "Development",
        readTime: "7 min read",
        content: `
JavaScript is a versatile and powerful programming language that is essential for modern web development. Whether you're building interactive websites, server-side applications, or mobile apps, understanding JavaScript fundamentals is crucial.

## What is JavaScript?

JavaScript is a high-level, interpreted programming language that enables you to create dynamic and interactive web content. It is one of the core technologies of the web, alongside HTML and CSS.

## Key Features of JavaScript

1. **Interactivity**: JavaScript allows you to create interactive elements on web pages, such as forms, animations, and games.
2. **Versatility**: It can be used for both client-side and server-side development.
3. **Event-Driven**: JavaScript can respond to user actions, such as clicks and key presses.
4. **Asynchronous Programming**: With features like Promises and async/await, JavaScript can handle asynchronous operations efficiently.

## Basic Syntax

Here are some fundamental concepts and syntax in JavaScript:

### Variables

Variables are used to store data. You can declare variables using 'var', 'let', or 'const'.

\`\`\`javascript
let message = "Hello, World!";
\`\`\`

### Functions

Functions are blocks of reusable code. You can define functions using the 'function' keyword.

\`\`\`javascript
function greet(name) {
    return "Hello, " + name + "!";
}
\`\`\`
      `
      }
  ];
